import Vue from 'vue';
import Component from 'vue-class-component';
import * as i18next from 'i18next';
import router from 'src/router';
import * as auth from 'core/auth';
import * as api from 'core/api';
import * as orders from 'core/orders';
import * as utils from 'core/utils';
import { G2S } from 'core/g2s';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const WithRender = require('./template.html');

export interface AccountOrdersComponentModel {
    state: string;
    orders: orders.OrderModel[];
    user: api.UserAccount | null;
}

@WithRender
@Component
export default class AccountOrdersComponent extends Vue {
    $auth: auth.AuthService; // Injected in G2S.init

    model: AccountOrdersComponentModel = {
        state: 'loading',
        orders: [],
        user: null
    }
    currentOrder: api.ReservationStatusResult | any = {
        reservationInfo: {
            billingPrice: 0
        },
        flight: {
            currencyCode: ''
        }
    };

    gaParams: utils.GoogleAnalyticsParams = new utils.GoogleAnalyticsParams();

    refundInfo: api.RefundInfo = {
        clientText: '',
        headerText: '',
        youPaidText: '',
        weWillRefundText: '',
        termText: '',
        buttonAcceptText: '',
        buttonRejectText: '',
        refundSum: 0,
        paidSum: 0,
        refundSumCurrency: ''
    };
    locale: string = 'ru';

    mounted() {
        window.document.title = i18next.t('title_orders');

        this.loadModel();
    }

    getOrderNumber(order: api.ReservationStatusResult): string {
        return i18next.t('label_order_with_number',
            <any>{ i: order.reservationInfo.reservationNumber }
        );
    }

    getSupportLink(order: api.ReservationStatusResult, value: string): string {
        return `/support/new/`;
    }

    loadModel() {
        this.model.state = 'loading';

        this.$auth.getCurrentUserAccount().then(
            (result: api.UserAccount) => {
                this.model.user = result;
            },
            () => {
                router.push('/signin');
            }
        );

        this.$auth.ordersList().then(
            (result: api.ReservationStatusResult[]) => {
                if (result && result.length > 0) {
                    this.model.state = 'results';

                    this.model.orders = new orders.OrderService().convertToOrders(result);
                } else {
                    this.model.state = 'empty';
                }
            },
            (error) => {
                this.model.state = 'error';

                if (error.response) {
                    console.log('XHR failed', error.response.status, error.response.data);
                } else {
                    console.log('XHR error', error.message);
                }
            }
        );
    }

    onRefundClick(order: api.ReservationStatusResult) {
        (<any>this).$ga.sendEvent('refund.tickets', 'click', 'refund.tickets.click.account', this.gaParams);
        (<any>this).$ga.sendEvent('support.new', 'click', 'support.new.click');
        (<any>this).$ga.reachGoal('support.new.click');
        (<any>this).$ga.reachGoal('support.new.click.account');
        router.push(this.getSupportLink(order, 'flight_refund'));
    }

    onClickSupport(order: api.ReservationStatusResult) {
        (<any>this).$ga.sendEvent('support.new', 'click', 'support.new.click');
        (<any>this).$ga.reachGoal('support.new.click');
        (<any>this).$ga.reachGoal('support.new.click.account');
        router.push(this.getSupportLink(order, 'flight_change'));
    }

    onCancelClick(order: api.ReservationStatusResult) {
        if (order.reservationInfo.autoVoidPossible) {
            this.currentOrder = order;
            this.$auth.getRefundInfo(this.locale, order.reservationInfo.reservationPublicId).then(
                (result)=>{
                    this.refundInfo = <api.RefundInfo>result;
                    (<any>this.$refs).dialog.show();
                },
                (error)=>{
                    location.href = '/account';
                }
            );
        } else {
            location.href = '/account';
        }
    }

    onAcceptCancelingOrder() {
        const reservationPublicId = this.currentOrder.reservationInfo.reservationPublicId;
        
        this.$auth.createVoid(reservationPublicId).then(
            (response) => {
                this.loadModel();
                this.$auth.getProcessStartedText(G2S.config.locale).then(
                    (result) => {
                        (<any>this.$refs).dialog.hide();
                        (<any>this.$root).$refs.messageDialog.show({
                            title: result.headerText,
                            message: result.messageText,
                            ok: result.buttonAcceptText
                        });
                    },
                    (error) => {
                        (<any>this.$refs).dialog.hide();
                        (<any>this.$root).$refs.messageDialog.show({
                            message: (error.response.data && error.response.data.localizedMessage) ? error.response.data.localizedMessage : i18next.t('error_server_tryagain')
                        });
                    }
                );
            },
            (error) => {
                (<any>this.$refs).dialog.hide();
                (<any>this.$root.$refs).messageDialog.show({
                    message: (error.response.data && error.response.data.localizedMessage) ? error.response.data.localizedMessage : i18next.t('error_server_tryagain')
                });
            }
        );
    }

    onClose() {
        (<any>this.$refs).dialog.hide();
    }

    onOrderSelect(order: orders.OrderModel) {
        router.push('/order/' + order.reservationInfo.reservationPublicId);
    }

    onRefundStatusesClick() {
        (<any>this).$ga.sendEvent('account', 'btn.click', 'btn.click.refund.status', this.gaParams);
    }
}
